import { useState, useMemo, useEffect } from 'react';
import { IconLoader2 } from '@tabler/icons-react';
import { api } from '../lib/api';
import MainCard from '../components/MainCard';
import PageHeader from '../components/PageHeader';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

type Distributor = {
  id: number;
  company_name: string;
  i_customer: number;
};

type SimStock = {
  starter_pack_name?: string;
  sim_type_name: string;
  is_activated: number;
  total_sim_cards: string;
};

type PivotedStock = {
  sim_type_name: string;
  active: number;
  inactive: number;
};

type GroupedStock = {
  starter_pack_name: string;
  items: PivotedStock[];
};

export function SimStockPage() {
  const [selectedDistributor, setSelectedDistributor] = useState<Distributor | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [withStarterPack, setWithStarterPack] = useState(false);
  const [selectedStarterPack, setSelectedStarterPack] = useState<string | null>(null);
  const [selectedSimType, setSelectedSimType] = useState<string | null>(null);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [isLoadingDistributors, setIsLoadingDistributors] = useState(false);
  const [stockData, setStockData] = useState<SimStock[]>([]);
  const [isLoadingStock, setIsLoadingStock] = useState(false);
  const [stockError, setStockError] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const loadDistributors = async () => {
      if (!searchTerm || searchTerm.length < 2) {
        setDistributors([]);
        return;
      }

      setIsLoadingDistributors(true);
      try {
        const response = await api.get<{ data: Distributor[] }>('/distributors', {
          params: { search: searchTerm, limit: 10 },
        });
        setDistributors(response.data.data || []);
        setShowDropdown(true);
      } catch (err) {
        console.error('Failed to load distributors', err);
        setDistributors([]);
      } finally {
        setIsLoadingDistributors(false);
      }
    };

    const timeoutId = setTimeout(loadDistributors, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    const loadStockData = async () => {
      if (!selectedDistributor?.i_customer) {
        setStockData([]);
        return;
      }

      setIsLoadingStock(true);
      setStockError(null);
      try {
        const response = await api.get<{ data: SimStock[] }>(
          `/distributors/${selectedDistributor.i_customer}/sim-stock`,
          { params: { withStarterPack } },
        );
        setStockData(response.data.data || []);
      } catch (err: any) {
        console.error('Failed to load stock data', err);
        setStockError(err.response?.data?.message || 'Failed to load stock data');
        setStockData([]);
      } finally {
        setIsLoadingStock(false);
      }
    };

    loadStockData();
  }, [selectedDistributor?.i_customer, withStarterPack]);

  const filterOptions = useMemo(() => {
    const starterPacks = new Set<string>();
    const simTypes = new Set<string>();

    stockData.forEach((item) => {
      if (item.starter_pack_name) starterPacks.add(item.starter_pack_name);
      if (item.sim_type_name) simTypes.add(item.sim_type_name);
    });

    return {
      starterPacks: Array.from(starterPacks).sort(),
      simTypes: Array.from(simTypes).sort(),
    };
  }, [stockData]);

  const processedData = useMemo(() => {
    const filteredData = stockData.filter((item) => {
      if (selectedStarterPack && item.starter_pack_name !== selectedStarterPack) return false;
      if (selectedSimType && item.sim_type_name !== selectedSimType) return false;
      return true;
    });

    if (withStarterPack) {
      const groups = new Map<string, Map<string, PivotedStock>>();

      filteredData.forEach((item) => {
        const starterPack = item.starter_pack_name || 'Unknown Starter Pack';
        const typeName = item.sim_type_name || 'Unknown Type';

        if (!groups.has(starterPack)) groups.set(starterPack, new Map());
        const group = groups.get(starterPack)!;

        if (!group.has(typeName)) {
          group.set(typeName, { sim_type_name: typeName, active: 0, inactive: 0 });
        }
        const entry = group.get(typeName)!;
        const count = parseInt(item.total_sim_cards, 10) || 0;

        if (item.is_activated === 1) entry.active += count;
        else entry.inactive += count;
      });

      const result: GroupedStock[] = [];
      groups.forEach((items, starterPackName) => {
        result.push({
          starter_pack_name: starterPackName,
          items: Array.from(items.values()).sort((a, b) => a.sim_type_name.localeCompare(b.sim_type_name)),
        });
      });
      return result.sort((a, b) => a.starter_pack_name.localeCompare(b.starter_pack_name));
    } else {
      const map = new Map<string, PivotedStock>();

      filteredData.forEach((item) => {
        const typeName = item.sim_type_name || 'Unknown';
        if (!map.has(typeName)) {
          map.set(typeName, { sim_type_name: typeName, active: 0, inactive: 0 });
        }
        const entry = map.get(typeName)!;
        const count = parseInt(item.total_sim_cards, 10) || 0;

        if (item.is_activated === 1) entry.active += count;
        else entry.inactive += count;
      });

      return Array.from(map.values()).sort((a, b) => a.sim_type_name.localeCompare(b.sim_type_name));
    }
  }, [stockData, withStarterPack, selectedStarterPack, selectedSimType]);

  return (
    <div>
      <PageHeader
        title="SIM Card Stock Management"
        subtitle="View SIM card stock levels by distributor"
      />

      <MainCard>
        <div className="mb-6 w-full">
          <div className="flex items-center gap-6 flex-nowrap w-full">
            <div className="relative w-[60%] min-w-0">
              <Label className="mb-1.5 block text-sm">Search Distributor</Label>
              <Input
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  if (!e.target.value) setSelectedDistributor(null);
                }}
                placeholder="Type to search for a distributor..."
              />
              {isLoadingDistributors && (
                <div className="absolute right-3 top-[38px]">
                  <IconLoader2 className="h-4 w-4 animate-spin" />
                </div>
              )}
              {showDropdown && distributors.length > 0 && (
                <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg max-h-60 overflow-auto">
                  {distributors.map((d) => (
                    <button
                      key={d.id}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
                      onClick={() => {
                        setSelectedDistributor(d);
                        setSearchTerm(`${d.company_name} (${d.i_customer})`);
                        setShowDropdown(false);
                        setSelectedStarterPack(null);
                        setSelectedSimType(null);
                      }}
                    >
                      {d.company_name} ({d.i_customer})
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 w-[40%] pt-6">
              <Checkbox
                id="starter-pack"
                checked={withStarterPack}
                onCheckedChange={(checked) => setWithStarterPack(checked === true)}
              />
              <Label htmlFor="starter-pack" className="text-base cursor-pointer">
                Group by Starter Pack
              </Label>
            </div>
          </div>
        </div>

        {selectedDistributor && (
          <div className="mb-6 w-full">
            <div className="flex gap-6 flex-nowrap w-full">
              {withStarterPack && (
                <div className="w-1/2 min-w-0">
                  <Label className="mb-1.5 block text-sm">Filter by Starter Pack</Label>
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    value={selectedStarterPack || ''}
                    onChange={(e) => setSelectedStarterPack(e.target.value || null)}
                  >
                    <option value="">All Starter Packs</option>
                    {filterOptions.starterPacks.map((sp) => (
                      <option key={sp} value={sp}>{sp}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className={cn('min-w-0', withStarterPack ? 'w-1/2' : 'w-full')}>
                <Label className="mb-1.5 block text-sm">Filter by SIM Type</Label>
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={selectedSimType || ''}
                  onChange={(e) => setSelectedSimType(e.target.value || null)}
                >
                  <option value="">All SIM Types</option>
                  {filterOptions.simTypes.map((st) => (
                    <option key={st} value={st}>{st}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {selectedDistributor && (
          <div className="mt-6">
            <h2 className="text-lg font-semibold mb-4">
              Stock for {selectedDistributor.company_name}
            </h2>

            {isLoadingStock ? (
              <div className="flex justify-center p-16">
                <IconLoader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : stockError ? (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>Failed to load stock data.</AlertDescription>
              </Alert>
            ) : (Array.isArray(processedData) && processedData.length === 0) ? (
              <Alert className="mb-4">
                <AlertDescription>No SIM card stock found matching your criteria.</AlertDescription>
              </Alert>
            ) : (
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SIM Type</TableHead>
                      <TableHead className="text-right">Active</TableHead>
                      <TableHead className="text-right">Inactive</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {withStarterPack ? (
                      (processedData as GroupedStock[]).map((group) => (
                        <>{/* Fragment key handled by first row */}
                          <TableRow key={group.starter_pack_name} className="bg-muted/50">
                            <TableCell colSpan={4} className="font-semibold">
                              {group.starter_pack_name}
                            </TableCell>
                          </TableRow>
                          {group.items.map((row) => (
                            <TableRow key={`${group.starter_pack_name}-${row.sim_type_name}`}>
                              <TableCell className="pl-8">{row.sim_type_name}</TableCell>
                              <TableCell className="text-right">
                                <Badge variant="success">{row.active}</Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <Badge variant="warning">{row.inactive}</Badge>
                              </TableCell>
                              <TableCell className="text-right font-semibold">
                                {row.active + row.inactive}
                              </TableCell>
                            </TableRow>
                          ))}
                        </>
                      ))
                    ) : (
                      (processedData as PivotedStock[]).map((row) => (
                        <TableRow key={row.sim_type_name}>
                          <TableCell className="font-medium">{row.sim_type_name}</TableCell>
                          <TableCell className="text-right">
                            <Badge variant="success">{row.active}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant="warning">{row.inactive}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {row.active + row.inactive}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </MainCard>
    </div>
  );
}
